/**
 * Verticality Check — plumb-line tracking along a column / mast / stack.
 *
 * Two-base workflow:
 *   1. Create a session (column name + tolerance mm).
 *   2. Take TWO base shots (Base A + Base B) at the bottom of the column
 *      face. Their horizontal separation defines the **reference plane**
 *      — a vertical plane containing the base line.
 *   3. Take check shots up the column. Each check shot reports the
 *      SIGNED perpendicular deviation from that reference plane, along
 *      with along-line offset and height.
 *
 * Two live visualisations:
 *   • Face view (looking along the base line) — signed perpendicular
 *     deviation on X, height on Y. This is the "lean profile".
 *   • Plan view (top-down) — base line A→B drawn, check shots plotted
 *     with tolerance envelope lines on either side.
 */
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus, Crosshair, Download, X,
} from "@phosphor-icons/react";
import { station, verticality as vertApi, target as targetApi, fmt } from "../lib/api";
import { ToolSplitLayout } from "../components/ToolSplitLayout";
import { PrintReportShell, PrintReportButton } from "../components/PrintReportShell";
import { AimPad } from "../components/AimPad";
import { useAuth } from "../lib/auth";
import { useLivePresence } from "../hooks/useLivePresence";
import {
  useAutoMeasureOnStop, useAutoMeasureState, AutoMeasureToggle,
} from "../hooks/useAutoMeasureOnStop";

// ---------- Session list card -----------------------------------------

const SessionCard = ({ session, onOpen, onDelete }) => {
  const worstMm = Math.abs(session.max_perpendicular_mm || 0);
  const tol = session.tolerance_mm || 5;
  const state = !session.reference_ready
    ? "pending"
    : worstMm <= tol ? "ok" : worstMm <= tol * 2 ? "warn" : "fail";
  const color = {
    ok: "#8DC63F", warn: "#FF9F0A", fail: "#FF3B30", pending: "#009E8E",
  }[state];
  const suffix = session.reference_ready
    ? `worst ${worstMm.toFixed(1)} mm · tol ±${tol.toFixed(1)} mm`
    : "baseline not set";
  return (
    <div
      data-testid={`vert-session-${session.id}`}
      className="group flex items-center justify-between rounded-md border border-[#2A2E39] bg-[#14161A] p-3 hover:border-[#009E8E]/50"
    >
      <button onClick={() => onOpen(session.id)} className="flex flex-1 items-start gap-3 text-left">
        <div className="h-10 w-1 rounded-full" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
        <div className="flex-1">
          <div className="text-sm font-bold uppercase text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            {session.name}
          </div>
          <div className="mt-0.5 text-[10px] uppercase tracking-widest text-[#8B939A]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
            {session.n_shots} shots · {session.n_checks} checks · {suffix}
          </div>
        </div>
      </button>
      <button
        data-testid={`vert-delete-${session.id}`}
        onClick={() => onDelete(session.id)}
        className="rounded-sm border border-transparent p-2 text-[#5A6065] opacity-0 transition-all group-hover:opacity-100 hover:border-[#FF3B30] hover:text-[#FF3B30]"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
};

// ---------- Face view — perpendicular deviation vs height -----------

export const FaceView = ({ shots, tolerance_mm, referenceReady }) => {
  const w = 320;
  const h = 340;
  const pad = { l: 50, r: 20, t: 20, b: 30 };
  const plotW = w - pad.l - pad.r;
  const plotH = h - pad.t - pad.b;

  const checks = shots.filter((s) => s.role === "check");
  const maxHeight = Math.max(1, ...checks.map((s) => s.height_m || 0));
  // Symmetric perp axis; floor at 2 × tolerance for a sensible envelope.
  const maxPerp = Math.max(
    tolerance_mm * 2,
    ...checks.map((s) => Math.abs(s.perpendicular_mm || 0)),
    5,
  );
  const cx = pad.l + plotW / 2;
  const py = (hMeter) => pad.t + plotH - (hMeter / maxHeight) * plotH;
  const px = (perpMm) => cx + (perpMm / maxPerp) * (plotW / 2);

  return (
    <div className="rounded-md border border-[#2A2E39] bg-[#0A0B0E] p-3">
      <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-widest">
        <span className="text-[#8B939A]" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
          Face view (perpendicular deviation)
        </span>
        <span className="text-[#8B939A]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
          ↑ {(maxHeight * 1000).toFixed(0)} mm · ↔ ± {maxPerp.toFixed(0)} mm
        </span>
      </div>
      <svg
        data-testid="vert-face-view"
        viewBox={`0 0 ${w} ${h}`}
        className="w-full"
        style={{ maxHeight: 340 }}
      >
        <defs>
          <marker id="vert-arrow-pass" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 Z" fill="#8DC63F" />
          </marker>
          <marker id="vert-arrow-fail" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 Z" fill="#FF3B30" />
          </marker>
        </defs>
        {/* Frame */}
        <rect x={pad.l} y={pad.t} width={plotW} height={plotH} fill="none" stroke="#2A2E39" strokeWidth="0.5" />

        {/* Vertical reference line at perp = 0 */}
        <line x1={cx} y1={pad.t} x2={cx} y2={pad.t + plotH} stroke="#009E8E" strokeWidth="1.2" opacity="0.8" />

        {/* Tolerance envelope */}
        <line x1={px(tolerance_mm)} y1={pad.t} x2={px(tolerance_mm)} y2={pad.t + plotH} stroke="#8DC63F" strokeWidth="0.7" strokeDasharray="3 3" opacity="0.6" />
        <line x1={px(-tolerance_mm)} y1={pad.t} x2={px(-tolerance_mm)} y2={pad.t + plotH} stroke="#8DC63F" strokeWidth="0.7" strokeDasharray="3 3" opacity="0.6" />
        {/* Filled tol band */}
        <rect
          x={px(-tolerance_mm)}
          y={pad.t}
          width={px(tolerance_mm) - px(-tolerance_mm)}
          height={plotH}
          fill="#8DC63F"
          opacity="0.05"
        />

        {/* Y axis labels */}
        <text x={pad.l - 6} y={pad.t + 4} textAnchor="end" fill="#4A5054" fontSize="8" fontFamily="JetBrains Mono, monospace">
          {(maxHeight * 1000).toFixed(0)} mm
        </text>
        <text x={pad.l - 6} y={pad.t + plotH + 2} textAnchor="end" fill="#4A5054" fontSize="8" fontFamily="JetBrains Mono, monospace">
          0 mm
        </text>
        {/* X axis labels */}
        <text x={pad.l} y={h - 8} textAnchor="start" fill="#FF3B30" fontSize="8" fontFamily="JetBrains Mono, monospace">
          −{maxPerp.toFixed(0)} mm
        </text>
        <text x={cx} y={h - 8} textAnchor="middle" fill="#009E8E" fontSize="8" fontFamily="JetBrains Mono, monospace">
          PLUMB
        </text>
        <text x={pad.l + plotW} y={h - 8} textAnchor="end" fill="#FF3B30" fontSize="8" fontFamily="JetBrains Mono, monospace">
          +{maxPerp.toFixed(0)} mm
        </text>

        {referenceReady && (
          <>
            {/* Base-line marker at z=0 */}
            <line
              x1={pad.l + 4}
              y1={py(0)}
              x2={pad.l + plotW - 4}
              y2={py(0)}
              stroke="#009E8E"
              strokeWidth="0.6"
              opacity="0.4"
              strokeDasharray="2 2"
            />
            {/* Trace line through checks (in shot order) */}
            {checks.length >= 2 && (
              <polyline
                points={[
                  `${cx},${py(0)}`,
                  ...checks.map((s) => `${px(s.perpendicular_mm || 0)},${py(s.height_m || 0)}`),
                ].join(" ")}
                fill="none"
                stroke="#8B939A"
                strokeWidth="1"
                strokeDasharray="3 2"
              />
            )}

            {/* Check-shot markers with directional arrows from plumb axis */}
            {checks.map((s, i) => {
              const cxp = px(s.perpendicular_mm || 0);
              const cyp = py(s.height_m || 0);
              const ok = s.within_tol;
              const fill = ok ? "#8DC63F" : "#FF3B30";
              const marker = ok ? "url(#vert-arrow-pass)" : "url(#vert-arrow-fail)";
              const stemLen = Math.abs(cxp - cx);
              return (
                <g key={s.id}>
                  {/* Arrow from plumb line (design) → actual shot */}
                  {stemLen >= 6 ? (
                    <line
                      x1={cx}
                      y1={cyp}
                      x2={cxp}
                      y2={cyp}
                      stroke={fill}
                      strokeWidth="1.8"
                      opacity="0.9"
                      markerEnd={marker}
                    />
                  ) : (
                    <line x1={cx} y1={cyp} x2={cxp} y2={cyp} stroke={fill} strokeWidth="1.2" opacity="0.6" />
                  )}
                  {/* Tail dot at plumb-line intersection */}
                  <circle cx={cx} cy={cyp} r="1.5" fill={fill} opacity="0.6" />
                  <circle cx={cxp} cy={cyp} r="4.5" fill={fill} stroke="#0A0B0E" strokeWidth="1.5" />
                  <text
                    x={cxp + (s.perpendicular_mm >= 0 ? 8 : -8)}
                    y={cyp + 3}
                    textAnchor={s.perpendicular_mm >= 0 ? "start" : "end"}
                    fill={fill}
                    fontSize="9"
                    fontFamily="JetBrains Mono, monospace"
                  >
                    {s.label || `#${i + 3}`}
                  </text>
                </g>
              );
            })}
          </>
        )}

        {!referenceReady && (
          <text
            x={cx}
            y={pad.t + plotH / 2}
            textAnchor="middle"
            fill="#5A6065"
            fontSize="11"
            fontFamily="Chakra Petch, sans-serif"
          >
            Take Base A + Base B to establish the reference
          </text>
        )}
      </svg>
    </div>
  );
};

// ---------- Plan view — top-down --------------------------------------

export const PlanView = ({ shots, tolerance_mm, session }) => {
  const w = 320;
  const h = 340;
  const pad = 30;
  const plotW = w - 2 * pad;
  const plotH = h - 2 * pad;

  const checks = shots.filter((s) => s.role === "check");
  const baseLen = session.baseline_length_m || 0;
  const maxAlong = Math.max(baseLen, ...checks.map((s) => s.along_m || 0), baseLen * 1.2 || 1);
  const minAlong = Math.min(0, ...checks.map((s) => s.along_m || 0));
  const alongRange = Math.max(0.001, maxAlong - minAlong);
  const maxPerpAbs = Math.max(
    tolerance_mm * 2,
    ...checks.map((s) => Math.abs(s.perpendicular_mm || 0)),
    5,
  );

  const along2x = (a) => pad + ((a - minAlong) / alongRange) * plotW;
  const perp2y = (p_mm) => pad + plotH / 2 - (p_mm / maxPerpAbs) * (plotH / 2);

  return (
    <div className="rounded-md border border-[#2A2E39] bg-[#0A0B0E] p-3">
      <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-widest">
        <span className="text-[#8B939A]" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
          Plan view (top-down)
        </span>
        <span className="text-[#8B939A]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
          along {(alongRange * 1000).toFixed(0)} mm · ⊥ ± {maxPerpAbs.toFixed(0)} mm
        </span>
      </div>
      <svg
        data-testid="vert-plan-view"
        viewBox={`0 0 ${w} ${h}`}
        className="w-full"
        style={{ maxHeight: 340 }}
      >
        <defs>
          <marker id="vplan-arrow-pass" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 Z" fill="#8DC63F" />
          </marker>
          <marker id="vplan-arrow-fail" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 Z" fill="#FF3B30" />
          </marker>
        </defs>
        {/* Frame */}
        <rect x={pad} y={pad} width={plotW} height={plotH} fill="none" stroke="#2A2E39" strokeWidth="0.5" />

        {session.reference_ready ? (
          <>
            {/* Reference base line A→B (perp = 0) */}
            <line
              x1={along2x(0)}
              y1={perp2y(0)}
              x2={along2x(baseLen)}
              y2={perp2y(0)}
              stroke="#009E8E"
              strokeWidth="2"
            />
            {/* Tolerance envelope */}
            <line x1={pad} y1={perp2y(tolerance_mm)} x2={pad + plotW} y2={perp2y(tolerance_mm)} stroke="#8DC63F" strokeWidth="0.7" strokeDasharray="3 3" opacity="0.6" />
            <line x1={pad} y1={perp2y(-tolerance_mm)} x2={pad + plotW} y2={perp2y(-tolerance_mm)} stroke="#8DC63F" strokeWidth="0.7" strokeDasharray="3 3" opacity="0.6" />
            <rect
              x={pad}
              y={perp2y(tolerance_mm)}
              width={plotW}
              height={perp2y(-tolerance_mm) - perp2y(tolerance_mm)}
              fill="#8DC63F"
              opacity="0.05"
            />
            {/* Base A/B markers */}
            <circle cx={along2x(0)} cy={perp2y(0)} r="5" fill="#009E8E" stroke="#0A0B0E" strokeWidth="1.5" />
            <text x={along2x(0)} y={perp2y(0) - 10} textAnchor="middle" fill="#009E8E" fontSize="9" fontFamily="JetBrains Mono, monospace">A</text>
            <circle cx={along2x(baseLen)} cy={perp2y(0)} r="5" fill="#009E8E" stroke="#0A0B0E" strokeWidth="1.5" />
            <text x={along2x(baseLen)} y={perp2y(0) - 10} textAnchor="middle" fill="#009E8E" fontSize="9" fontFamily="JetBrains Mono, monospace">B</text>

            {/* Bearing badge */}
            <text
              x={along2x(baseLen / 2)}
              y={perp2y(0) + 16}
              textAnchor="middle"
              fill="#4A5054"
              fontSize="8"
              fontFamily="JetBrains Mono, monospace"
            >
              {(session.baseline_bearing_deg || 0).toFixed(0)}° · {(baseLen * 1000).toFixed(0)} mm
            </text>

            {/* Check shots — arrows from base line to actual position */}
            {checks.map((s, i) => {
              const cxp = along2x(s.along_m || 0);
              const cyp = perp2y(s.perpendicular_mm || 0);
              const anchorX = along2x(s.along_m || 0);
              const anchorY = perp2y(0);
              const ok = s.within_tol;
              const fill = ok ? "#8DC63F" : "#FF3B30";
              const marker = ok ? "url(#vplan-arrow-pass)" : "url(#vplan-arrow-fail)";
              const stemLen = Math.abs(cyp - anchorY);
              return (
                <g key={s.id}>
                  {stemLen >= 6 ? (
                    <line
                      x1={anchorX}
                      y1={anchorY}
                      x2={cxp}
                      y2={cyp}
                      stroke={fill}
                      strokeWidth="1.8"
                      opacity="0.9"
                      markerEnd={marker}
                    />
                  ) : (
                    <line x1={anchorX} y1={anchorY} x2={cxp} y2={cyp} stroke={fill} strokeWidth="1.2" opacity="0.6" />
                  )}
                  <circle cx={anchorX} cy={anchorY} r="1.5" fill={fill} opacity="0.6" />
                  <circle cx={cxp} cy={cyp} r="4" fill={fill} stroke="#0A0B0E" strokeWidth="1.5" />
                  <text
                    x={cxp + 6}
                    y={cyp - 4}
                    fill={fill}
                    fontSize="9"
                    fontFamily="JetBrains Mono, monospace"
                  >
                    {s.label || `#${i + 3}`}
                  </text>
                </g>
              );
            })}
          </>
        ) : (
          <text
            x={w / 2}
            y={h / 2}
            textAnchor="middle"
            fill="#5A6065"
            fontSize="11"
            fontFamily="Chakra Petch, sans-serif"
          >
            Reference line will appear after Base A + Base B
          </text>
        )}
      </svg>
    </div>
  );
};

// ---------- Shot table row -------------------------------------------

const ShotRow = ({ shot, index, onDelete }) => {
  const roleTag = {
    base_a: { label: "BASE A", cls: "text-[#009E8E] border-[#009E8E]/40 bg-[#009E8E]/10" },
    base_b: { label: "BASE B", cls: "text-[#009E8E] border-[#009E8E]/40 bg-[#009E8E]/10" },
    check:  shot.within_tol
      ? { label: "PASS", cls: "text-[#8DC63F] border-[#8DC63F]/40 bg-[#8DC63F]/10" }
      : { label: "FAIL", cls: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10" },
  }[shot.role] || { label: "—", cls: "text-[#8B939A] border-[#8B939A]/40 bg-transparent" };
  return (
    <tr data-testid={`vert-shot-row-${shot.id}`} className="border-b border-[#1A1D24]">
      <td className="p-2 text-white">#{index + 1}</td>
      <td className="p-2 text-white">{shot.label || "—"}</td>
      <td className="p-2 text-right font-mono">{shot.along_m == null ? "—" : (shot.along_m * 1000).toFixed(0)}</td>
      <td className="p-2 text-right font-mono font-bold text-white">
        {shot.perpendicular_mm == null
          ? "—"
          : `${shot.perpendicular_mm > 0 ? "+" : ""}${shot.perpendicular_mm.toFixed(1)}`}
      </td>
      <td className="p-2 text-right font-mono">{shot.height_m == null ? "—" : (shot.height_m * 1000).toFixed(0)}</td>
      <td className="p-2 text-center">
        <span className={`inline-block rounded-sm border px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${roleTag.cls}`}>
          {roleTag.label}
        </span>
      </td>
      <td className="p-2 text-right">
        <button
          data-testid={`vert-shot-delete-${shot.id}`}
          onClick={() => onDelete(shot.id)}
          className="rounded-sm border border-transparent p-1 text-[#5A6065] hover:border-[#FF3B30] hover:text-[#FF3B30]"
          title="Delete shot"
        >
          <X size={12} weight="bold" />
        </button>
      </td>
    </tr>
  );
};

// ---------- Main panel --------------------------------------------------

export default function VerticalityCheck({ onBack }) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null); // Full session object
  const [newName, setNewName] = useState("");
  const [newTol, setNewTol] = useState("5.0");
  const [measuring, setMeasuring] = useState(false);
  const [tolInput, setTolInput] = useState("5.0");
  const [labelInput, setLabelInput] = useState("");
  const [targetSetting, setTargetSetting] = useState(null);

  // Live presence — supervisor watch page picks up any active
  // Verticality session so a foreman on the office laptop can see
  // shots land in real time. Heartbeats only while a session is open.
  useLivePresence({
    enabled: !!current?.id,
    tool: "verticality",
    sessionId: current?.id,
    sessionName: current?.name,
    username: user?.username,
    role: user?.role,
    counters: {
      shots: current?.shots?.length || 0,
      reference_ready: !!current?.reference_ready,
      max_perp_mm: current?.shots
        ? Math.max(0, ...current.shots
            .filter((s) => s.role === "check")
            .map((s) => Math.abs(s.perpendicular_mm || 0)))
        : 0,
    },
  });

  const refreshSessions = useCallback(async () => {
    try {
      const list = await vertApi.listSessions();
      setSessions(list);
    } catch {
      toast.error("Could not load sessions");
    }
  }, []);

  const refreshCurrent = useCallback(async (sid) => {
    try {
      const s = await vertApi.get(sid);
      setCurrent(s);
      setTolInput(String(s.tolerance_mm));
    } catch {
      setCurrent(null);
    }
  }, []);

  // Track active target for QA stamping on each shot.
  useEffect(() => {
    const load = async () => {
      try {
        const [s, ps] = await Promise.all([targetApi.get(), targetApi.presets()]);
        const active = s.preset_id === "custom"
          ? s.custom
          : ps.find((p) => p.id === s.preset_id) || ps[0];
        setTargetSetting(active);
      } catch { /* offline */ }
    };
    load();
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => { refreshSessions(); }, [refreshSessions]);

  const createSession = async () => {
    if (!newName.trim()) {
      toast.error("Give the column a name");
      return;
    }
    const tol = parseFloat(newTol);
    if (!Number.isFinite(tol) || tol <= 0) {
      toast.error("Tolerance must be a positive number of mm");
      return;
    }
    try {
      const s = await vertApi.create(newName.trim(), tol);
      setNewName("");
      await refreshSessions();
      await refreshCurrent(s.id);
      toast.success("Session ready — take Base A at one end of the column face");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create session");
    }
  };

  const takeShot = async () => {
    if (!current) return;
    setMeasuring(true);
    try {
      const result = await station.measure("coord");
      if (result.x == null || result.y == null || result.z == null) {
        toast.error("No coord returned — is the TS16 aimed at the target?");
        return;
      }
      await saveShot(result);
    } catch { toast.error("Shot failed"); }
    finally { setMeasuring(false); }
  };

  /** Persist a shot given an already-taken measurement. Used by both
   *  the manual Take Shot button and the auto-measure-on-stop hook. */
  const saveShot = useCallback(async (result) => {
    if (!current || result?.x == null) return;
    const nextIdx = current.shots.length;   // 0 → base_a, 1 → base_b, ≥2 → check
    const updated = await vertApi.addShot(current.id, {
      label: labelInput.trim() || null,
      x: result.x, y: result.y, z: result.z,
      hz: result.hz, v: result.v, sd: result.sd,
      target_preset: targetSetting?.label || null,
      target_perpendicular_mm: targetSetting?.perpendicular_mm || 0,
    });
    setCurrent(updated);
    setLabelInput("");
    refreshSessions();
    if (nextIdx === 0) toast.success("Base A captured — now Base B");
    else if (nextIdx === 1) {
      if (updated.warning) toast.error(updated.warning, { duration: 6000 });
      else toast.success(`Baseline ready · ${(updated.baseline_length_m * 1000).toFixed(0)} mm`);
    }
  }, [current, labelInput, targetSetting, refreshSessions]);

  // Auto-measure on prism stop — off by default, remembered per tool.
  const [autoMeasure, setAutoMeasure] = useAutoMeasureState("vert");
  useAutoMeasureOnStop({
    enabled: autoMeasure && !!current,
    onFire: saveShot,
  });

  const deleteShot = async (shot_id) => {
    if (!current) return;
    try {
      const s = await vertApi.removeShot(current.id, shot_id);
      setCurrent(s);
      refreshSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Delete this verticality session?")) return;
    try {
      await vertApi.remove(sid);
      if (current?.id === sid) setCurrent(null);
      refreshSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const saveTolerance = async () => {
    if (!current) return;
    const t = parseFloat(tolInput);
    if (!Number.isFinite(t) || t <= 0) return;
    try {
      const s = await vertApi.setTolerance(current.id, t);
      setCurrent(s);
      refreshSessions();
      toast.success(`Tolerance set to ±${t.toFixed(1)} mm`);
    } catch {
      toast.error("Could not update tolerance");
    }
  };

  const nudge = async (direction) => {   // eslint-disable-line no-unused-vars
    try { await station.nudge(direction, false); } catch { /* silent */ }
  };


  // ---- Render list view ----

  if (!current) {
    return (
      <div className="mx-auto max-w-3xl p-6" data-testid="vert-page-list">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">Verticality Check</div>
            <h2 className="text-2xl font-bold uppercase tracking-tight text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              Plumb-line inspection
            </h2>
          </div>
          <button
            data-testid="vert-back-list"
            onClick={onBack}
            className="rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
          >
            ← Toolbox
          </button>
        </div>

        {/* New session card */}
        <div className="mb-6 rounded-md border border-[#2A2E39] bg-gradient-to-br from-[#1A1D24] to-[#14161A] p-4">
          <div className="mb-3 text-[10px] uppercase tracking-widest text-[#8B939A]">New session</div>
          <div className="grid gap-3 sm:grid-cols-[1fr,120px,140px]">
            <input
              data-testid="vert-new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Column ID (e.g. C-14 SW leg)"
              className="rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white placeholder:text-[#4A5054] focus:border-[#009E8E] focus:outline-none"
            />
            <div className="flex items-center gap-2">
              <input
                data-testid="vert-new-tol"
                type="number"
                step="0.1"
                value={newTol}
                onChange={(e) => setNewTol(e.target.value)}
                className="w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white focus:border-[#009E8E] focus:outline-none"
              />
              <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">mm</span>
            </div>
            <button
              data-testid="vert-create-btn"
              onClick={createSession}
              className="flex items-center justify-center gap-2 rounded-sm border border-[#009E8E] bg-[#009E8E]/10 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#009E8E] hover:bg-[#009E8E]/20"
              style={{ fontFamily: "Chakra Petch, sans-serif" }}
            >
              <Plus size={12} weight="bold" /> Create
            </button>
          </div>
          <div className="mt-2 text-[10px] text-[#5A6065]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
            Typical steel column tolerance: ±5 mm. Precision fabrication: ±2 mm. Chimney/mast: ±10 mm.
          </div>
        </div>

        <div className="mb-2 text-[10px] uppercase tracking-widest text-[#8B939A]">Sessions</div>
        {sessions.length === 0 ? (
          <div className="rounded-md border border-dashed border-[#2A2E39] p-6 text-center text-[11px] text-[#5A6065]">
            No sessions yet. Create one above to start plumbing.
          </div>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => (
              <SessionCard
                key={s.id}
                session={s}
                onOpen={refreshCurrent}
                onDelete={deleteSession}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- Session view ----
  const shots = current.shots || [];
  const nextIdx = shots.length;
  const nextRoleLabel =
    nextIdx === 0 ? "Set Base A"
    : nextIdx === 1 ? "Set Base B"
    : "Take check";
  const nextRoleColor =
    nextIdx < 2
      ? "border-[#FF9F0A] bg-[#FF9F0A]/10 text-[#FF9F0A] hover:bg-[#FF9F0A]/20"
      : "border-[#009E8E] bg-[#009E8E]/10 text-[#009E8E] hover:bg-[#009E8E]/20";
  const headline =
    nextIdx === 0 ? "Waiting for Base A"
    : nextIdx === 1 ? "Waiting for Base B"
    : `${shots.length - 2} check${shots.length - 2 === 1 ? "" : "s"} · Baseline set`;

  return (
    <ToolSplitLayout
      left={
        <div className="mx-auto max-w-4xl space-y-4" data-testid="vert-page-session">
          <PrintReportShell
            toolName="Verticality Check"
            sessionName={current.name}
            passFail={
              !current.reference_ready ? null
              : shots.length <= 2 ? null
              : Math.abs(current.max_perpendicular_mm || 0) > current.tolerance_mm ? "FAIL" : "PASS"
            }
            headline={
              current.reference_ready
                ? `Worst offset ${(current.max_perpendicular_mm ?? 0).toFixed(1)} mm · tol ±${current.tolerance_mm} mm · ${Math.max(0, shots.length - 2)} checks`
                : "Baseline not set — no results yet"
            }
            surveyor={user?.username || user?.display_name || "—"}
          >
          <div className="flex items-center justify-between no-print">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">
                Verticality · {current.name}
              </div>
              <h2
                className="text-2xl font-bold uppercase tracking-tight text-white"
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
              >
                {headline}
              </h2>
            </div>
            {current.reference_ready && shots.length > 2 && (
              <PrintReportButton testid="vert-print-btn" />
            )}
          </div>

          {/* Baseline summary */}
          {current.reference_ready && (
            <div className="grid grid-cols-3 gap-3 rounded-md border border-[#009E8E]/30 bg-[#009E8E]/5 p-3">
              <Stat label="Baseline length" value={`${(current.baseline_length_m * 1000).toFixed(1)} mm`} />
              <Stat label="Baseline bearing" value={`${current.baseline_bearing_deg.toFixed(1)}°`} />
              <Stat label="Reference" value="ARMED" tone="ok" />
            </div>
          )}

          {/* Visualisations */}
          <div className="grid gap-4 md:grid-cols-2">
            <FaceView shots={shots} tolerance_mm={current.tolerance_mm} referenceReady={current.reference_ready} />
            <PlanView shots={shots} tolerance_mm={current.tolerance_mm} session={current} />
          </div>

          {/* Shot table */}
          <div className="overflow-hidden rounded-md border border-[#2A2E39] bg-[#14161A]">
            <table className="w-full text-[11px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              <thead>
                <tr className="border-b border-[#2A2E39] bg-[#0A0B0E] text-[#8B939A]">
                  <th className="p-2 text-left uppercase tracking-widest">#</th>
                  <th className="p-2 text-left uppercase tracking-widest">Label</th>
                  <th className="p-2 text-right uppercase tracking-widest">Along (mm)</th>
                  <th className="p-2 text-right uppercase tracking-widest">⊥ Deviation (mm)</th>
                  <th className="p-2 text-right uppercase tracking-widest">Height (mm)</th>
                  <th className="p-2 text-center uppercase tracking-widest">Role</th>
                  <th className="p-2 uppercase tracking-widest"></th>
                </tr>
              </thead>
              <tbody className="text-[#B5BEC5]">
                {shots.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-[#5A6065]">
                      No shots yet — hit &quot;Set Base A&quot; to begin.
                    </td>
                  </tr>
                ) : (
                  shots.map((s, i) => (
                    <ShotRow key={s.id} shot={s} index={i} onDelete={deleteShot} />
                  ))
                )}
              </tbody>
            </table>
          </div>
          </PrintReportShell>
        </div>
      }
      right={
        <>
          {/* Session nav row */}
          <div className="flex items-center justify-between gap-2">
            <button
              data-testid="vert-back-session"
              onClick={() => setCurrent(null)}
              className="flex-1 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
            >
              ← Sessions
            </button>
            <a
              data-testid="vert-download-csv"
              href={vertApi.csvUrl(current.id)}
              className="flex items-center gap-1 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
            >
              <Download size={12} weight="bold" /> CSV
            </a>
          </div>

          {/* Primary shot button — thumb-reachable, colour changes with
              the next-role. */}
          <AutoMeasureToggle
            value={autoMeasure}
            onChange={setAutoMeasure}
            testid="vert-auto-shot-toggle"
          />
          <button
            data-testid="vert-take-shot"
            disabled={measuring}
            onClick={takeShot}
            className={`flex min-h-[80px] items-center justify-center gap-2 rounded-lg border-2 px-4 py-4 text-base font-bold uppercase tracking-widest transition-all ${
              measuring
                ? "cursor-wait border-[#5A6065] bg-[#14161A] text-[#5A6065]"
                : nextRoleColor
            }`}
            style={{ fontFamily: "Chakra Petch, sans-serif" }}
          >
            <Crosshair size={22} weight="bold" />
            {measuring ? "Measuring…" : nextRoleLabel}
          </button>

          <div className="grid grid-cols-[1fr,88px] gap-2">
            <label className="block">
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[#8B939A]">
                Shot label
              </span>
              <input
                data-testid="vert-label"
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                placeholder={
                  nextIdx === 0 ? "Base A"
                  : nextIdx === 1 ? "Base B"
                  : "L3 · +6.5m"
                }
                className="w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white placeholder:text-[#4A5054] focus:border-[#009E8E] focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[#8B939A]">
                ± Tol mm
              </span>
              <input
                data-testid="vert-tol-input"
                type="number"
                step="0.1"
                value={tolInput}
                onChange={(e) => setTolInput(e.target.value)}
                onBlur={saveTolerance}
                className="w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-2 py-2 font-mono text-sm text-white focus:border-[#009E8E] focus:outline-none"
              />
            </label>
          </div>

          <AimPad tool="verticality" sessionId={current.id} />

          {/* Contextual hints */}
          {current.warning && (
            <div className="rounded-sm border border-[#FF3B30] bg-[#FF3B30]/10 px-3 py-2 text-[10px] uppercase tracking-widest text-[#FF3B30]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              ⚠ {current.warning}
            </div>
          )}
          {nextIdx === 0 && !current.warning && (
            <div className="rounded-sm border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 px-3 py-2 text-[10px] uppercase tracking-widest text-[#FFB84D]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              ⓘ Aim at one end of the column&apos;s bottom face. Sets one end of the reference line.
            </div>
          )}
          {nextIdx === 1 && !current.warning && (
            <div className="rounded-sm border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 px-3 py-2 text-[10px] uppercase tracking-widest text-[#FFB84D]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              ⓘ Aim at the OTHER end of the same bottom face — ≥20 mm from Base A.
            </div>
          )}
        </>
      }
    />
  );
}

const Stat = ({ label, value, tone }) => {
  const color = tone === "ok" ? "#8DC63F" : tone === "warn" ? "#FF9F0A" : "#FFFFFF";
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.25em] text-[#8B939A]">{label}</div>
      <div className="mt-0.5 font-mono text-base" style={{ color }}>
        {value}
      </div>
    </div>
  );
};
